import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Feature, FeatureCollection } from "geojson";
import { groupBy } from "lodash";
import type mapboxgl from "mapbox-gl";
import { FeatureIdentifier } from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { baseDrawControlModes } from "fond/draw/helper";
import { GenericValidator, validateCreatedFeatures, ValidationFunction } from "fond/draw/validation";
import { HistoryContext, useHistory } from "fond/history";
import LayerSelection from "fond/map/LayerSelection/LayerSelection";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch } from "fond/utils/hooks";
import { BlockSpinner, ConfirmModal } from "fond/widgets";

import DrawHandler, { DrawHandlerProps } from "../DrawHandler";

import { useMultiLayerDrawingStyles } from "./useMultiLayerDrawingStyles";
import { useMultiLayerFeatures } from "./useMultiLayerFeatures";

const { constants: Constants } = MapboxDraw;

export interface MultiLayerEditHandlerRef {
  reset: () => void;
  getFeatures: () => Feature[];
  save: () => Promise<unknown[]>;
}

interface MultiLayerEditHandlerProps extends Omit<DrawHandlerProps, "initialFeatures"> {
  versionId: string;
  initialFeatures?: DrawHandlerProps["initialFeatures"];
}

// TODO - sometimes the Snap button is not correctl enabled.

/**
 * This component is responsible for handling the editing of multiple layers simultaneously.
 * It retrieves the drawing styles for each specified layer and can be used to render an editing UI.
 *
 * If initialFeatures is not provided, it will automatically fetch features for all layers
 * specified in options.allLayers.
 */
const MultiLayerEditHandler = forwardRef<MultiLayerEditHandlerRef, MultiLayerEditHandlerProps>(
  ({ versionId, initialFeatures, ...drawHandlerProps }: MultiLayerEditHandlerProps, ref) => {
    const { map, drawControl } = useContext(MapContext);
    const dispatch = useAppDispatch();
    const validatorRef = useRef<{
      validator: GenericValidator;
      validationFunctions: Map<string, ValidationFunction>;
    } | null>(null);

    const { clear: clearHistory } = useHistory<FeatureCollection>(HistoryContext);
    const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);

    // Track the editing layer to detect changes
    const previousLayerId = useRef<string | undefined>(drawControl.current?.options?.editOptions?.editingLayerId);
    // Track the mode key to detect if modes actually changed
    const previousModeKey = useRef<string | undefined>();

    // Define the layer keys that we want to edit simultaneously
    const layerKeys = useMemo(() => drawHandlerProps.options?.editOptions?.allLayers?.map((layer) => layer.ID) || [], [drawHandlerProps.options]);

    // Fetch features only if initialFeatures not provided
    const shouldFetch = !initialFeatures;
    const { features: fetchedFeatures, isLoading } = useMultiLayerFeatures(versionId, shouldFetch ? layerKeys : []);

    // Use provided features or fetched features
    const rawFeatures = initialFeatures || fetchedFeatures;

    // Validate features before passing to DrawHandler
    const finalFeatures = useMemo(() => {
      if (!rawFeatures || rawFeatures.length === 0) return rawFeatures;
      if (!drawHandlerProps.options?.editOptions?.allLayers) return rawFeatures;

      // Build validation functions map
      const validationFunctions = new Map<string, ValidationFunction>();
      drawHandlerProps.options.editOptions.allLayers.forEach((layer) => {
        if (layer.validate) {
          validationFunctions.set(layer.ID, layer.validate);
        }
      });

      if (validationFunctions.size === 0) return rawFeatures;

      // Create validator with all features
      const validator = new GenericValidator(rawFeatures);

      // Validate each feature and set properties directly
      return rawFeatures.map((feature) => {
        const layerId = feature.properties?.layerId;
        if (!layerId || !validationFunctions.has(layerId)) return feature;

        const validationFn = validationFunctions.get(layerId)!;
        const result = validator.validate(feature, validationFn);

        // Set validation properties directly on the feature
        return {
          ...feature,
          properties: {
            ...feature.properties,
            [DRAWING_CONSTANTS.Valid]: result.valid.toString(),
            [DRAWING_CONSTANTS.ValidationErrors]: result.valid ? "" : result.errors.join("; "),
          },
        };
      });
    }, [rawFeatures, drawHandlerProps.options?.editOptions?.allLayers]);

    const sources: FeatureIdentifier[] = useMemo(
      () =>
        layerKeys.map((id) => ({
          source: "project-layers", // TODO - constant
          sourceLayer: id,
        })),
      [layerKeys]
    );

    // Get the drawing styles for all layers at once
    const layerStylesMap = useMultiLayerDrawingStyles(versionId, layerKeys);

    // Combine all layer styles with filtered default styles
    const styles = useMemo(() => {
      // Filter out default styles that we're replacing with layer-specific styles
      const filtered = defaultStyles.filter(
        ({ id }) =>
          ![
            "gl-draw-line-inactive",
            // "gl-draw-line-active",
            "gl-draw-point-inactive",
            "gl-draw-point-active",
            "gl-draw-point-point-stroke-inactive",
            "gl-draw-point-point-stroke-active",
            "gl-draw-polygon-inactive",
            "gl-draw-polygon-active",
            "gl-draw-polygon-stroke-inactive",
            // This is used for both Active POINTS, but also LineString vertices
            // Can we differentiate these?
            // OR we just make it yellow like selected?
            // "gl-draw-point-stroke-active", // TODO - can we override this with an X similar to qGIS
          ].includes(id)
      );

      // Collect all styles from all layers (including per-layer validation styles)
      const allLayerStyles = Array.from(layerStylesMap.values())
        .filter((layerStyles): layerStyles is NonNullable<typeof layerStyles> => layerStyles !== undefined)
        .flat();

      // Combine: layer styles (including validation), then defaults
      // Layer styles are reversed so validation styles render on top
      const combined = [...allLayerStyles.reverse(), ...filtered];
      return combined;
    }, [layerStylesMap]);

    // Internal function that performs the actual reset
    const performReset = useCallback(() => {
      if (!drawControl.current || !finalFeatures) return;

      // Remove all current features
      drawControl.current.deleteAll();

      // Add back final features
      if (finalFeatures.length > 0) {
        drawControl.current.add({
          type: "FeatureCollection",
          features: finalFeatures,
        });
      }
      clearHistory();
      setResetConfirmationOpen(false);
    }, [drawControl, finalFeatures, clearHistory]);

    // Expose reset, getFeatures, and save methods via ref
    useImperativeHandle(ref, () => ({
      /**
       * Reset function that clears all current features from the draw control and re-adds the initial features.
       * It also clears the history to prevent undoing back to the previous state before reset.
       */
      reset: () => {
        setResetConfirmationOpen(true);
      },
      /**
       * getFeatures function that retrieves the current features from the draw control. This can be used to get the latest state of all features being edited across multiple layers.
       * It returns an array of GeoJSON features currently in the draw control.
       * Note that this function retrieves features directly from the draw control, which is the source of truth for the current editing state. This ensures that any unsaved changes are included in the returned features.
       */
      getFeatures: () => {
        if (!drawControl.current) return [];
        return drawControl.current.getAll().features;
      },
      /**
       * Save function that groups features by their layerId and dispatches an import action for each layer.
       * It first retrieves all features from the draw control, groups them by their layerId property, and then creates a file for each layer to be imported.
       */
      save: async () => {
        const fc = drawControl.current?.getAll();
        if (!fc) return [];

        const featuresByLayer = groupBy(fc.features, (f) => f.properties?.layerId);

        const promises = Object.keys(featuresByLayer).map((layerKey) => {
          const cleanedCollection = removeDrawingProperties(featureCollection(featuresByLayer[layerKey]));
          const file = new File([JSON.stringify(cleanedCollection)], "geojson.json", {
            type: "text/plain",
          });
          return dispatch(
            addImport({
              versionId,
              layerKey,
              displayName: layerKey,
              files: [file],
            })
          );
        });
        return Promise.all(promises);
      },
    }));

    // Handle validation on feature creation
    const handleValidateCreatedFeature = useCallback(
      (event: MapboxDraw.DrawCreateEvent) => {
        if (!drawControl.current) return;

        // Get the created feature IDs from the event
        const createdFeatureIds = event.features.map((f) => f.id as string);

        // Use the validation helper to validate created features
        validateCreatedFeatures(drawControl.current, createdFeatureIds, drawHandlerProps.options?.editOptions?.allLayers, validatorRef, map);
      },
      [drawControl, drawHandlerProps.options?.editOptions?.allLayers, map]
    );

    // Listen for draw.create events and validate after properties are set
    useEffect(() => {
      if (!map) return;

      // Use setTimeout to ensure validation runs AFTER DrawHandler's handleOnCreate sets the properties
      const handleCreate = (event: MapboxDraw.DrawCreateEvent) => {
        setTimeout(() => {
          handleValidateCreatedFeature(event);
        }, 0);
      };

      map.on(Constants.events.CREATE, handleCreate);

      // eslint-disable-next-line consistent-return
      return () => {
        map.off(Constants.events.CREATE, handleCreate);
      };
    }, [map, handleValidateCreatedFeature]);

    // Handle layer selection changes from LayerSelection component
    const handleLayerChange = useCallback(
      (event: {
        layerId: string;
        parentLayerId?: string;
        sublayerFilter?: mapboxgl.ExpressionSpecification;
        editingLayer?: { ID: string; options?: Record<string, unknown> };
      }) => {
        if (!drawControl.current?.options || !map) return;

        const { layerId, parentLayerId, sublayerFilter, editingLayer } = event;

        // Preserve colocation setting, editing layers, and sublayer metadata from current options
        const { topologicalEditing } = drawControl.current.options;
        const currentEditingLayerIds = drawControl.current.options.editOptions?.editingLayerIds;
        const currentEditableSublayers = drawControl.current.options.editOptions?.editableSublayers;

        // Merge base options with layer-specific options
        // @ts-expect-error - Custom property added in DrawHandler
        const baseOptions = drawControl.current.baseOptions || {};
        const mergedOptions = {
          ...baseOptions,
          ...(editingLayer?.options || {}),
          // Set these AFTER layer options to ensure they're not overridden
          editOptions: {
            ...baseOptions.editOptions,
            ...(editingLayer?.options?.editOptions || {}),
            editingLayerId: layerId,
            allLayers: drawControl.current.options.editOptions?.allLayers,
            editingLayerIds: currentEditingLayerIds, // Preserve editing layers
            editableSublayers: currentEditableSublayers, // Preserve sublayer metadata
          },
          // Preserve colocation setting
          topologicalEditing,
          // Add sublayer filter if present
          sublayerFilter: sublayerFilter || undefined,
          parentLayerId: parentLayerId || undefined,
        };

        // Delete properties that were in previous options but aren't in merged options
        // This ensures layer-specific overrides are properly removed when switching layers
        const currentOptions = drawControl.current.options;
        Object.keys(currentOptions).forEach((key) => {
          if (!(key in mergedOptions) && key !== "baseOptions") {
            delete currentOptions[key as keyof typeof currentOptions];
          }
        });

        // Mutate existing options object instead of replacing it
        // This ensures the reference stays the same for modes that cache it
        Object.assign(currentOptions, mergedOptions);

        // Deselect any selected features when changing layers
        drawControl.current.changeMode("simple_select");
        map.fire("draw.modechange", {
          type: "draw.modechange",
          mode: "simple_select",
        });
      },
      [map, drawControl]
    );

    useEffect(() => {
      if (!map) return;

      map.on("draw.layerchange", handleLayerChange);

      // eslint-disable-next-line consistent-return
      return () => {
        map.off("draw.layerchange", handleLayerChange);
      };
    }, [map, handleLayerChange]);

    // Reinitialize the draw control when the editing layer changes and modes are different
    useEffect(() => {
      if (!map) return;
      if (!drawControl.current) return;

      const currentLayerId = drawControl.current.options?.editOptions?.editingLayerId;
      // Look up the full layer object from allLayers
      const currentLayer = drawControl.current.options?.editOptions?.allLayers?.find((layer) => layer.ID === currentLayerId);

      // Get the mode key to determine if modes changed
      const currentModeKey = currentLayer?.ModeKey;

      // Check if the editing layer has changed AND modes are different
      const layerChanged = currentLayerId && currentLayerId !== previousLayerId.current;
      const modesChanged = currentModeKey !== previousModeKey.current;

      if (layerChanged && modesChanged) {
        // Determine which modes to use
        const modesToUse = currentLayer?.Modes ? { ...baseDrawControlModes, ...currentLayer.Modes } : baseDrawControlModes;
        // Preserve existing features
        const existingFeatures = drawControl.current.getAll();

        // Preserve current options
        const currentOptions = { ...drawControl.current.options };

        // Get the current mode before removing the control
        const currentMode = drawControl.current.getMode();

        // Remove the old control
        if (map.hasControl(drawControl.current)) {
          drawControl.current.changeMode("simple_select");
          map.removeControl(drawControl.current);
        }

        // Create new draw control with updated modes
        drawControl.current = new MapboxDraw({
          displayControlsDefault: false,
          ...currentOptions,
          modes: modesToUse,
          styles: currentOptions.styles || defaultStyles,
        });

        // Add the new control to the map
        map.addControl(drawControl.current);

        // Restore the features
        if (existingFeatures.features.length > 0) {
          drawControl.current.set(existingFeatures);
        }

        // Restore the previous mode
        try {
          drawControl.current.changeMode(currentMode as string);
        } catch {
          // If the mode doesn't exist in the new control, default to simple_select
          drawControl.current.changeMode("simple_select");
        }

        // Update the tracked layer ID and mode key
        previousLayerId.current = currentLayerId;
        previousModeKey.current = currentModeKey;
      }
    }, [drawControl, map, drawControl.current?.options?.editOptions?.editingLayerId]);

    // Enhance options with default snapGetFeatures if not provided
    const enhancedOptions = useMemo(() => {
      const options = drawHandlerProps.options || {};

      // If snapGetFeatures is not defined, provide a default that filters by visible layers
      if (!options.snapOptions?.snapGetFeatures) {
        return {
          ...options,
          snapOptions: {
            ...options.snapOptions,
            snapGetFeatures: () => {
              if (!drawControl.current) return [];

              const allFeatures = drawControl.current.getAll().features;
              const visibleLayerIds = drawControl.current.options?.visibleLayerIds;

              // If no visibility filter is set, return all features
              if (!visibleLayerIds || visibleLayerIds.length === 0) {
                return allFeatures;
              }

              // Filter features to only include those from visible layers
              return allFeatures.filter((feature) => {
                const layerId = feature.properties?.layerId;
                return layerId && visibleLayerIds.includes(layerId);
              });
            },
          },
        };
      }

      return options;
    }, [drawHandlerProps.options, drawControl]);

    // Show loading spinner while fetching features
    if (shouldFetch && isLoading) {
      return <BlockSpinner />;
    }

    return (
      <>
        {resetConfirmationOpen && (
          <ConfirmModal
            open
            header="Reset Changes"
            content="Are you sure you want to reset all changes? This will revert all features back to their initial state."
            confirmText="Reset"
            cancelText="Cancel"
            onConfirm={performReset}
            onCancel={() => setResetConfirmationOpen(false)}
            color="error"
          />
        )}
        <MapOverlay showOverlay={false} centerContent={false}>
          <LayerSelection />
        </MapOverlay>
        <DrawHandler
          disableHoverEffects
          {...drawHandlerProps}
          options={enhancedOptions}
          initialFeatures={finalFeatures}
          sources={sources}
          styles={styles}
        />
      </>
    );
  }
);

MultiLayerEditHandler.displayName = "MultiLayerEditHandler";

export default MultiLayerEditHandler;
